'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import React, { createContext, useContext, useState } from 'react';
import { Link } from './link';
import { cn } from '../../lib/utils';

// Define table context
interface TableContextProps {
  bleed: boolean;
  dense: boolean;
  grid: boolean;
  striped: boolean;
  variant?: 'default' | 'crimson' | 'mint' | 'blue' | 'black';
}

const TableContext = createContext<TableContextProps>({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
  variant: 'default',
});

// Define table variants
const tableContainerVariants = cva(['flow-root']);

const tableWrapperVariants = cva([
  '-mx-4 overflow-x-auto whitespace-nowrap',
]);

const tableInnerVariants = cva(
  ['inline-block min-w-full align-middle'],
  {
    variants: {
      bleed: {
        true: '',
        false: 'sm:px-4',
      },
    },
    defaultVariants: {
      bleed: false,
    },
  }
);

const tableVariants = cva([
  'min-w-full text-left text-sm/6 text-foreground',
]);

export interface TableProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof tableInnerVariants> {
  bleed?: boolean;
  dense?: boolean;
  grid?: boolean;
  striped?: boolean;
  variant?: 'default' | 'crimson' | 'mint' | 'blue' | 'black';
  className?: string;
}

export function Table({
  bleed = false,
  dense = false,
  grid = false,
  striped = false,
  variant = 'default',
  className,
  children,
  ...props
}: TableProps) {
  return (
    <TableContext.Provider
      value={{ bleed, dense, grid, striped, variant }}
    >
      <div className={cn(tableContainerVariants())}>
        <div
          {...props}
          className={cn(tableWrapperVariants(), className)}
        >
          <div className={cn(tableInnerVariants({ bleed }))}>
            <table className={cn(tableVariants())}>{children}</table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

// Define table head variants
const tableHeadVariants = cva(['text-muted-foreground']);

export interface TableHeadProps
  extends React.ComponentPropsWithoutRef<'thead'> {
  className?: string;
}

export function TableHead({ className, ...props }: TableHeadProps) {
  return (
    <thead
      {...props}
      className={cn(tableHeadVariants(), className)}
    />
  );
}

export interface TableBodyProps
  extends React.ComponentPropsWithoutRef<'tbody'> {
  className?: string;
}

export function TableBody({ className, ...props }: TableBodyProps) {
  return <tbody {...props} className={cn(className)} />;
}

// Define table row context
interface TableRowContextProps {
  href?: string;
  target?: string;
  title?: string;
}

const TableRowContext = createContext<TableRowContextProps>({
  href: undefined,
  target: undefined,
  title: undefined,
});

// Define table row variants
const tableRowVariants = cva(
  [
    // Base styles - match the original behavior more closely
    'focus-within:relative focus-within:z-10',
  ],
  {
    variants: {
      isLink: {
        true: 'has-[[data-row-link]:focus]:outline-2 has-[[data-row-link]:focus]:-outline-offset-2 has-[[data-row-link]:focus]:outline-ring',
        false: '',
      },
      striped: {
        true: 'even:bg-muted',
        false: '',
      },
      hover: {
        stripedLink: 'hover:bg-muted/80',
        plainLink: 'hover:bg-muted',
        none: '',
      },
      variant: {
        default: 'has-[[data-row-link]:focus]:outline-ring',
        crimson: 'has-[[data-row-link]:focus]:outline-flame',
        mint: 'has-[[data-row-link]:focus]:outline-sunlight',
        blue: 'has-[[data-row-link]:focus]:outline-ocean',
        black:
          'has-[[data-row-link]:focus]:outline-black dark:has-[[data-row-link]:focus]:outline-white',
      },
    },
    defaultVariants: {
      isLink: false,
      striped: false,
      hover: 'none',
      variant: 'default',
    },
  }
);

export interface TableRowProps
  extends React.ComponentPropsWithoutRef<'tr'> {
  href?: string;
  target?: string;
  title?: string;
  className?: string;
}

export function TableRow({
  href,
  target,
  title,
  className,
  ...props
}: TableRowProps) {
  const { striped, variant } = useContext(TableContext);

  const isLink = !!href;
  const hover =
    isLink && striped ? 'stripedLink' : isLink ? 'plainLink' : 'none';

  return (
    <TableRowContext.Provider value={{ href, target, title }}>
      <tr
        {...props}
        className={cn(
          tableRowVariants({ isLink, striped, hover, variant }),
          className
        )}
      />
    </TableRowContext.Provider>
  );
}

// Define table header variants
const tableHeaderVariants = cva(
  [
    'border-b border-border px-4 py-2 font-medium first:pl-2 last:pr-2',
  ],
  {
    variants: {
      grid: {
        true: 'border-l border-l-border first:border-l-0',
        false: '',
      },
      bleed: {
        true: '',
        false: 'sm:first:pl-1 sm:last:pr-1',
      },
    },
    defaultVariants: {
      grid: false,
      bleed: false,
    },
  }
);

export interface TableHeaderProps
  extends React.ComponentPropsWithoutRef<'th'> {
  className?: string;
}

export function TableHeader({
  className,
  ...props
}: TableHeaderProps) {
  const { bleed, grid } = useContext(TableContext);

  return (
    <th
      {...props}
      className={cn(tableHeaderVariants({ grid, bleed }), className)}
    />
  );
}

// Define table cell variants
const tableCellVariants = cva(
  ['relative px-4 first:pl-2 last:pr-2'],
  {
    variants: {
      striped: {
        true: '',
        false: 'border-b border-border',
      },
      grid: {
        true: 'border-l border-l-border first:border-l-0',
        false: '',
      },
      dense: {
        true: 'py-2.5',
        false: 'py-4',
      },
      bleed: {
        true: '',
        false: 'sm:first:pl-1 sm:last:pr-1',
      },
    },
    defaultVariants: {
      striped: false,
      grid: false,
      dense: false,
      bleed: false,
    },
  }
);

export interface TableCellProps
  extends React.ComponentPropsWithoutRef<'td'> {
  className?: string;
}

export function TableCell({
  className,
  children,
  ...props
}: TableCellProps) {
  const { bleed, dense, grid, striped } = useContext(TableContext);
  const { href, target, title } = useContext(TableRowContext);
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null);

  return (
    <td
      ref={href ? setCellRef : undefined}
      {...props}
      className={cn(
        tableCellVariants({ striped, grid, dense, bleed }),
        className
      )}
    >
      {href && (
        <Link
          data-row-link
          href={href}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-none"
        />
      )}
      {children}
    </td>
  );
}

export {
  tableContainerVariants,
  tableWrapperVariants,
  tableInnerVariants,
  tableVariants,
  tableHeadVariants,
  tableRowVariants,
  tableHeaderVariants,
  tableCellVariants,
};
